import React, { useRef } from 'react';
import { Modal, Button, Form } from 'antd';
import './DiscountConfigModal.css';
import DiscountConfigForm from '../Forms/DiscountConfigForm';
import { MODAL_CONFIG, UI_TEXT, CONSOLE_MESSAGES } from './discountConfigConstants';
import logger from 'utils/logger';

/**
 * DiscountConfigModal Component
 *
 * Modal component for configuring discount options including:
 * - Minimum purchase requirements (amount, quantity, or none)
 * - Maximum discount uses (limited or unlimited)
 * - Validity period (number of days)
 *
 * Uses the reusable DiscountConfigForm component internally.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether the modal is visible
 * @param {Function} props.onHide - Callback function to close the modal
 * @param {Object} [props.discountConfig={}] - Current discount configuration from backend API
 * @param {Function} props.onSave - Callback function called when saving configuration, receives transformed payload
 * @param {boolean} [props.loading=false] - Loading state for save operation
 * @param {boolean} [props.isOnboarding=false] - Whether this is used in onboarding flow (affects default values)
 * @returns {JSX.Element} The discount configuration modal component
 *
 * @example
 * <DiscountConfigModal
 *   show={isModalOpen}
 *   onHide={() => setIsModalOpen(false)}
 *   discountConfig={store.discount_config}
 *   onSave={handleSaveDiscountConfig}
 *   loading={isSaving}
 * />
 */
// NOSONAR: React components must use PascalCase naming convention
const DiscountConfigModal = ({
  show,
  onHide,
  discountConfig = {},
  onSave,
  loading = false,
  isOnboarding = false
}) => {
  // Store form instance reference to access validation
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const lastConfigRef = useRef(null);

  /**
   * Handles form submission by getting values from the form and calling onSave.
   *
   * @returns {void}
   */
  const handleSubmit = () => {
    // Validate all form fields before submission
    form.validateFields().then(() => {
      // Get the transformed values from DiscountConfigForm
      if (formRef.current && formRef.current.getValues) {
        const transformedValues = formRef.current.getValues();
        if (onSave) {
          onSave(transformedValues);
        }
      } else if (lastConfigRef.current) {
        // Use last known config if ref method not available
        if (onSave) {
          onSave(lastConfigRef.current);
        }
      } else {
        logger.warn('DiscountConfigForm ref not available and no last config');
      }
    }).catch(err => {
      // Log validation errors for debugging
      logger.error(CONSOLE_MESSAGES.VALIDATION_FAILED, err);
    });
  };

  /**
   * Handles discount config changes from the form.
   * Stores the transformed values for submission.
   *
   * @param {Object} transformedConfig - Transformed discount configuration
   * @returns {void}
   */
  const handleConfigChange = transformedConfig => {
    // Store the transformed config for submission
    lastConfigRef.current = transformedConfig;
  };

  /**
   * Renders the discount configuration modal with form sections.
   * Contains three main sections: minimum purchase requirements,
   * maximum discount uses, and validity period.
   *
   * @returns {JSX.Element} Modal component with form
   */
  return (
    <Modal
      title={UI_TEXT.MODAL_TITLE}
      open={show}
      onCancel={onHide}
      width={MODAL_CONFIG.WIDTH}
      footer={[
        // Cancel button - closes modal without saving
        <Button key={MODAL_CONFIG.BUTTON_KEYS.CANCEL} onClick={onHide} disabled={loading}>
          {UI_TEXT.BUTTON_CANCEL}
        </Button>,
        // Save button - validates and submits form
        <Button
          key={MODAL_CONFIG.BUTTON_KEYS.SUBMIT}
          type={MODAL_CONFIG.BUTTON_TYPES.PRIMARY}
          loading={loading}
          onClick={handleSubmit}
        >
          {UI_TEXT.BUTTON_SAVE_CONFIGURATION}
        </Button>
      ]}
      className='discount-config-modal'
    >
      {/* Main form container with vertical layout */}
      <DiscountConfigForm
        form={form}
        discountConfig={discountConfig}
        isOnboarding={isOnboarding}
        inline={false}
        onChange={handleConfigChange}
        ref={formRef}
      />
    </Modal>
  );
};

export default DiscountConfigModal;
